import config, { AppType, getViteEnv } from "../config";
import { DocumentTemplateType } from "../../../api/src/types";

const isPostnup = config.appType === AppType.POSTNUP;
const isPrenup = config.appType === AppType.PRENUP;

// ─── Core brand names ────────────────────────────────────────────────
export const BRAND_NAME = isPostnup ? "Postnup.com" : "HelloPrenup";
export const BRAND_LEGAL_ENTITY = isPostnup
  ? "Postnup.com"
  : "Hello Prenup, Inc.";
// The name as the lockup's wordmark sets it — its own case, which prose must not
// copy. The wordmarks are drawn, so this is their accessible name.
export const BRAND_WORDMARK = isPostnup ? "POSTNUP.com" : "HELLO PRENUP";
export const SIGNUP_STATE_PLACEHOLDER = "State of residence";
export const MARRIAGE_DATE_QUESTION_ID = isPostnup
  ? config.postnupMarriageDateQuestionId
  : config.weddingDateQuestionId;
export const MARRIAGE_STATUS_LINK_TEXT = isPostnup
  ? "We aren’t married yet. →"
  : "We are already married. →";

// ─── Document type labels ────────────────────────────────────────────
export const SIGNUP_WELCOME_DEMO_VIDEO_ID = "UJsfmmoDTzo";
export const getSignupWelcomePostnupAttorneyCopy = (stateName?: string) =>
  `${BRAND_NAME} attorneys are required. Lucky for you—they’re all highly-rated and vetted from top law firms${stateName ? ` in ${stateName}` : ""}.`;

export const DOCUMENT_TYPE = isPostnup ? "postnup" : "prenup";
export const getRepresentationDirectoryCopy = (
  requiresRepresentation: boolean,
) => ({
  partner: requiresRepresentation
    ? "You and your partner will each hire your own attorneys for Representation on your own accounts. Each party needs independent legal counsel, so you may not use the same attorney."
    : "Your attorney represents you individually. Your partner selects any attorney they need through their own account.",
  how: requiresRepresentation
    ? `Each party is required to hire an Independent Partner Attorney through our ${BRAND_NAME} Attorney Network to complete your ${DOCUMENT_TYPE}.`
    : "Choose an independent attorney to review and finalize your agreement. Your partner selects any attorney they need through their own account.",
});
export const ATTORNEY_DASHBOARD_INTRO = `This is your dashboard! Here you’ll find relevant information for your ${DOCUMENT_TYPE} & process. Use the checklist to complete your steps in order. Need support? `;
export const ATTORNEY_BOOKING_REMINDER = `${isPostnup ? "Don't forget" : "Because you indicated that you plan to hire an attorney, don't forget"} that your ${DOCUMENT_TYPE} can't be finalized until your lawyer has signed off. Let's get your attorney booked to keep things moving.`;

export const SIGNUP_WELCOME_PARTNER_INVITE_COPY = `Invite your partner to create a ${DOCUMENT_TYPE} with you on their own account.`;
export const SIGNUP_WELCOME_PARTNER_COLLABORATION_COPY = `Work with your partner on your ${DOCUMENT_TYPE}, each using your own account.`;
export const PAYMENT_SUCCESS_MESSAGE = `Let’s get this ${DOCUMENT_TYPE} party started.`;
export const ADD_ON_PAYMENT_SUCCESS_MESSAGE = `Let’s continue on your ${DOCUMENT_TYPE} journey.`;
export const QUESTIONNAIRE_INSTRUCTIONS = isPostnup
  ? "Choose the answer that best fits your situation. Don't see exactly what you want? Pick the closest option. You'll be able to customize those sections with your attorney."
  : "Choose the answer that best fits your situation. If none of the answers fit your situation, you’re welcome to hire an attorney for Representation to create specific language for your situation.";
export const DOCUMENT_TYPE_FULL = isPostnup
  ? "postnuptial agreement"
  : "prenuptial agreement";
export const DOCUMENT_TYPE_CAPITALIZED = isPostnup ? "Postnup" : "Prenup";
export const PRENUP_IMMIGRATION_NOTICE = {
  before: "When you purchase a HelloPrenup prenup, you'll get access to one ",
  highlighted: "FREE consultation",
  after:
    " with an immigration attorney who can help answer your immigration-related questions. Plus, an option to add in immigration clauses if necessary, for an additional charge.",
};
export const PRENUP_IMMIGRATION_INFO_URL =
  "https://helloprenup.com/what-to-know-about-immigration-and-your-prenup/";
export const DOCUMENT_TYPE_FULL_CAPITALIZED = isPostnup
  ? "Postnuptial Agreement"
  : "Prenuptial Agreement";
export const PREMARITAL_OR_POSTMARITAL = isPostnup
  ? "postmarital"
  : "premarital";

export const GENERIC_PARTNER_NAME = "partner";
export const PARTNER_NAME_POSTNUP = "spouse";
export const PARTNER_NAME_PRENUP = "fiancé";
export const PARTNER_NAME = isPostnup
  ? PARTNER_NAME_POSTNUP
  : PARTNER_NAME_PRENUP;
export const PARTNER_NAME_CAPITALIZED_POSTNUP = "Spouse";
export const PARTNER_NAME_CAPITALIZED_PRENUP = "Fiancé";
export const PARTNER_NAME_CAPITALIZED = isPostnup
  ? PARTNER_NAME_CAPITALIZED_POSTNUP
  : PARTNER_NAME_CAPITALIZED_PRENUP;

// ─── URLs ────────────────────────────────────────────────────────────
// TODO: Replace postnup.com links with actual postnup.com URLs once available
export const BRAND_DOMAIN = isPostnup ? "postnup.com" : "helloprenup.com";
export const APP_DOMAIN = isPostnup ? "app.postnup.com" : "app.helloprenup.com";
export const TERMS_URL = isPostnup
  ? "https://postnup.com/terms-of-use/" // TODO: confirm postnup.com terms URL
  : "https://helloprenup.com/terms-of-use/";
export const PRIVACY_URL = isPostnup
  ? "https://postnup.com/privacy-policy/" // TODO: confirm postnup.com privacy URL
  : "https://helloprenup.com/privacy-policy/";
export const HELP_URL = isPostnup
  ? "https://help.postnup.com/en/" // TODO: confirm postnup.com help URL
  : "https://help.helloprenup.com/en/";
export const PARTNER_ACCOUNT_HELP_URL =
  "https://help.helloprenup.com/en/articles/11683574-can-i-just-complete-the-questionnaire-myself-and-have-my-fiance-sign-it";
export const CONSULTATION_URL =
  "https://calendly.com/support_team_/helloprenup-consultation";
export const SUPPORT_CALL_URL =
  "https://calendly.com/support_team_/support-call";
export const SUPPORT_EMAIL = isPostnup
  ? "support@postnup.com" // TODO: confirm postnup.com support email
  : "support@helloprenup.com";
export const NOTARY_INFO_URL = isPostnup
  ? "https://postnup.com/notarization/" // TODO: confirm postnup.com notary URL
  : "https://helloprenup.com/notary/";

// ─── Helper ──────────────────────────────────────────────────────────
export { isPostnup, isPrenup };
export const AVERAGE_SAVED_AMOUNT = isPostnup ? "$7,500" : "$8,000";
export const MAIN_PRODUCT_PRICE = isPostnup ? "$2499" : "$599";

export const isDocumentTemplatePostnup = (templateId: string) => {
  return templateId === config.postnupTemplateId;
};

export const getDocumentType = (templateId: string) => {
  return isDocumentTemplatePostnup(templateId) ? "Postnup" : "Prenup";
};

export const getBrandNameForDocumentTemplateType = (
  documentTemplateType: DocumentTemplateType,
) =>
  documentTemplateType === DocumentTemplateType.POSTNUP
    ? "Postnup.com"
    : "HelloPrenup";

export const getDocumentTypeFullForDocumentTemplateType = (
  documentTemplateType: DocumentTemplateType,
) =>
  documentTemplateType === DocumentTemplateType.POSTNUP
    ? "postnuptial agreement"
    : "prenuptial agreement";

export const getRedirectionBaseUrl = (
  documentTemplateType: DocumentTemplateType,
) => {
  const hostname = window.location.hostname;
  if (hostname === "localhost") {
    switch (documentTemplateType) {
      case DocumentTemplateType.PRENUP:
        return (
          getViteEnv("VITE_PRENUP_WEB_BASE_URL") ??
          getViteEnv("VITE_WEB_BASE_URL") ??
          "http://localhost:5173"
        );
      case DocumentTemplateType.POSTNUP:
        return (
          getViteEnv("VITE_POSTNUP_WEB_BASE_URL") ??
          getViteEnv("VITE_WEB_BASE_URL") ??
          "http://localhost:5174"
        );
      default:
        return "";
    }
  } else if (
    hostname === "dev.helloprenup.com" ||
    hostname === "dev.postnup.com"
  ) {
    switch (documentTemplateType) {
      case DocumentTemplateType.PRENUP:
        return "https://dev.helloprenup.com";
      case DocumentTemplateType.POSTNUP:
        return "https://dev.postnup.com";
      default:
        return "";
    }
  } else if (
    hostname === "staging.helloprenup.com" ||
    hostname === "staging.postnup.com"
  ) {
    switch (documentTemplateType) {
      case DocumentTemplateType.PRENUP:
        return "https://staging.helloprenup.com";
      case DocumentTemplateType.POSTNUP:
        return "https://staging.postnup.com";
      default:
        return "";
    }
  } else if (
    hostname === "app.helloprenup.com" ||
    hostname === "app.postnup.com"
  ) {
    switch (documentTemplateType) {
      case DocumentTemplateType.PRENUP:
        return "https://app.helloprenup.com";
      case DocumentTemplateType.POSTNUP:
        return "https://app.postnup.com";
      default:
        return "";
    }
  } else {
    return "";
  }
};

export const POSTNUP_REPRESENTATION_INCLUDED =
  "Included with your postnup—one independent attorney for each partner.";

export const getAttorneyDirectoryCopy = (
  serviceType: "REPRESENTATION" | "Q_A" | null,
  requiresRepresentation: boolean,
  showQA = true,
) => {
  const representation = getRepresentationDirectoryCopy(requiresRepresentation);
  if (serviceType === "REPRESENTATION")
    return {
      how: representation.how,
      intro: `the attorney you’d like to finalize your agreement with by choosing “Pick” below. ${representation.partner}`,
    };
  if (serviceType === "Q_A")
    return {
      intro:
        "the attorney you’d like to meet for a Q&A session by choosing “Pick” below. Your attorney advises you individually; your partner can book their own session through their account.",
      how: "Meet with an independent attorney for a scheduled Q&A session about your rights and options. Q&A does not include document review, editing, signing, or notarization.",
    };
  if (!showQA)
    return {
      intro:
        "Representation for document review and customization, then select your attorney. Each attorney advises one partner individually.",
      how: representation.how,
    };
  return {
    intro:
      "a quick Q&A session for legal guidance, or Representation for document review and customization. Compare services below, then choose your attorney. Each attorney advises one partner individually.",
    how: `Our ${BRAND_NAME} Attorney Network offers Q&A sessions for specific questions and Representation to review and finalize your agreement. Choose the service you need when viewing an attorney’s availability.`,
  };
};
